import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getSiteConfig, getProjects, getPublications } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LoadingPage, NotFoundPage } from "@/components/PageStates";
import { motion } from "framer-motion";
import { ArrowLeft, Dna, Cpu, HeartPulse, Calendar, ArrowRight, BookOpen, FlaskConical } from "lucide-react";
import DOMPurify from "dompurify";

const ICON_MAP = { Dna, Cpu, HeartPulse };

export default function ResearchDetailPage() {
  const { index } = useParams();
  const [axis, setAxis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [relatedPubs, setRelatedPubs] = useState([]);

  useEffect(() => {
    setLoading(true);
    getSiteConfig("research").then(r => {
      const ax = r.data?.data?.axes?.[parseInt(index)];
      setAxis(ax || null);
    }).catch(() => setAxis(null)).finally(() => setLoading(false));
    getProjects().then(r => setRelatedProjects(r.data)).catch(() => {});
    getPublications().then(r => setRelatedPubs(r.data)).catch(() => {});
  }, [index]);

  if (loading) return <LoadingPage />;
  if (!axis) return <NotFoundPage title="Axe de recherche introuvable" message="Cet axe de recherche n'existe pas ou a été modifié." backLabel="Retour à la recherche" backTo="/#research" />;

  const IconComp = ICON_MAP[axis.icon] || Dna;

  return (
    <div data-testid="research-detail-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <div className="bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9] pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/#research" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Retour</Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center"><IconComp className="w-7 h-7 text-white" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 mb-1">Axe de recherche {parseInt(index) + 1}</p>
              <h1 className="font-['Quicksand'] text-3xl sm:text-4xl font-medium text-white">{axis.title}</h1>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 space-y-12">
        <section className="grid md:grid-cols-2 gap-8">
          {axis.image_url && <img src={axis.image_url} alt={axis.title} className="w-full h-64 md:h-80 object-cover rounded-xl shadow-sm" />}
          <div>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-4">Description</h2>
            <p className="text-base text-slate-600 leading-relaxed">{axis.desc}</p>
            {axis.content && <div className="mt-4 prose prose-sm max-w-none text-slate-600" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(axis.content) }} />}
          </div>
        </section>
        {relatedProjects.length > 0 && (
          <section>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2"><FlaskConical className="w-5 h-5 text-[#1670B9]" /> Projets associés</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {relatedProjects.slice(0, 4).map(p => (
                <Link to={`/project/${p.id}`} key={p.id} className="bg-white rounded-xl p-5 border border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2">{p.summary}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1670B9] mt-3">Voir le projet <ArrowRight className="w-3 h-3" /></span>
                </Link>
              ))}
            </div>
          </section>
        )}
        {relatedPubs.length > 0 && (
          <section>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#74CDD9]" /> Publications associées</h2>
            <div className="space-y-3">
              {relatedPubs.slice(0, 5).map(pub => (
                <div key={pub.id} className="bg-white rounded-xl p-4 border border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-800">{pub.title}</h3>
                  {pub.authors && <p className="text-xs text-[#1670B9] mt-1">{pub.authors}</p>}
                  {pub.year && <span className="text-xs text-slate-400 flex items-center gap-1 mt-1"><Calendar className="w-3 h-3" />{pub.year}</span>}
                </div>
              ))}
            </div>
            <Link to="/publications" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1670B9] mt-4 hover:gap-3 transition-all">Voir toutes les publications <ArrowRight className="w-4 h-4" /></Link>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}
